import { select, mouse as d3mouse, event as d3event } from 'd3-selection'
import { drag } from 'd3-drag'
import cloneDeep from 'lodash/cloneDeep'

const degrees = (angle) => (angle * 180) / Math.PI
const radians = (angle) => (angle * Math.PI) / 180

class MapObjectDrag {
  constructor(map) {
    this.map = map
    select(map.container.node()).call(
      drag()
        .on('drag', () => this.onDrag())
        .on('start', () => this.onDragStart())
        .on('end', () => this.onDragEnd())
    )
  }

  update(map) {
    this.map = map
  }

  onDragStart() {
    if (d3event.sourceEvent.srcElement.id !== 'floorImage') return
    const { map } = this

    if (map.handles) {
      const mouse = d3mouse(map.container.node())
      const handle = map.handles.reduce((result, h) => {
        if (
          mouse[0] >= h.cx - h.r &&
          mouse[0] <= h.cx + h.r &&
          mouse[1] >= h.cy - h.r &&
          mouse[1] <= h.cy + h.r
        ) {
          return h
        }

        return result
      }, null)

      this.bi = cloneDeep(map.blockInstances.byId[Object.keys(map.selectedIds)[0]])
      this.resizeHandle = handle

      if (handle.cursor === '-webkit-grab') {
        map.container.style('cursor', '-webkit-grabbing')
      }
    }
  }

  onDragEnd() {
    if (this.resizeHandle && this.resizeHandle.cursor === '-webkit-grab') {
      this.map.container.style('cursor', '-webkit-grab')
    }

    this.resizeHandle = null
  }

  onDrag() {
    const { map } = this

    if (this.resizeHandle) {
      switch (this.resizeHandle.location) {
        case 'top':
        case 'bottom':
          return this.handleVerticalResize()

        case 'left':
        case 'right':
          return this.handleHorizontalResize()

        case 'topLeft':
        case 'topRight':
        case 'bottomRight':
        case 'bottomLeft':
          return this.handleDiagonalResize(d3event)

        case 'rotate':
          return this.rotate()

        default:
        // do nothing
      }
    }

    const dx = map.imageScale.invert(d3event.dx) / map.transform.k
    const dy = map.imageScale.invert(d3event.dy) / map.transform.k
    map.actions.onDrag({ dx, dy })
  }

  handleVerticalResize() {
    const { map, bi } = this
    const { x, y } = d3event
    const { cx, cy, location } = this.resizeHandle
    const top = location === 'top'
    const dx1 = 2 * (x - cx)
    const dy1 = 2 * (y - cy)
    const c = Math.sqrt(dx1 * dx1 + dy1 * dy1)
    const beta = degrees(Math.atan2(dy1, dx1)) + 90
    const alfa = 180 + bi.map.rotation - beta
    const dy = c * Math.cos(radians(alfa))
    let h = bi.map.h + (top ? -1 : 1) * map.imageScale.invert(dy / map.transform.k)

    if (h < 1) h = 1

    map.actions.updateMapObjects({
      floorId: map.floorId,
      updatedObjects: [{ ...bi, map: { ...bi.map, h: Math.round(h) } }],
    })
  }

  handleHorizontalResize() {
    const { map, bi } = this
    const { x, y } = d3event
    const { cx, cy, location } = this.resizeHandle
    const left = location === 'left'
    const dx1 = 2 * (x - cx)
    const dy1 = 2 * (y - cy)
    const c = Math.sqrt(dx1 * dx1 + dy1 * dy1)
    const beta = degrees(Math.atan2(dy1, dx1)) + 90
    const alfa = 180 + bi.map.rotation - beta
    const dx = c * Math.sin(radians(alfa))
    let w = bi.map.w + (left ? -1 : 1) * map.imageScale.invert(dx / map.transform.k)

    if (w < 1) w = 1

    map.actions.updateMapObjects({
      floorId: map.floorId,
      updatedObjects: [{ ...bi, map: { ...bi.map, w: Math.round(w) } }],
    })
  }

  handleDiagonalResize() {
    const { map, bi, resizeHandle } = this
    const { x, y } = d3event
    const { cx, cy, location } = resizeHandle
    const dx1 = 2 * (x - cx)
    const dy1 = 2 * (y - cy)
    const c = Math.sqrt(dx1 * dx1 + dy1 * dy1)
    const beta = degrees(Math.atan2(dy1, dx1)) + 90
    const alfa = 180 + bi.map.rotation - beta
    const dy = c * Math.cos(radians(alfa))
    const dx = c * Math.sin(radians(alfa))
    const left = ['topLeft', 'bottomLeft'].includes(location)
    const top = ['topLeft', 'topRight'].includes(location)

    let w
    let h

    if (Math.abs(dx) > Math.abs(dy)) {
      w = bi.map.w + (left ? -1 : 1) * map.imageScale.invert(dx / map.transform.k)
      h = (bi.map.h * w) / bi.map.w
    } else {
      h = bi.map.h + (top ? -1 : 1) * map.imageScale.invert(dy / map.transform.k)
      w = (bi.map.w * h) / bi.map.h
    }

    if (w < 1) w = 1
    if (h < 1) h = 1

    map.actions.updateMapObjects({
      floorId: map.floorId,
      updatedObjects: [{ ...bi, map: { ...bi.map, h: Math.round(h), w: Math.round(w) } }],
    })
  }

  rotate() {
    const { map, bi } = this
    const { x, y } = d3event
    const cx = map.dx * map.transform.k + map.xScale(bi.map.x)
    const cy = map.dy * map.transform.k + map.yScale(bi.map.y)
    let rotation = (Math.atan2(y - cy, x - cx) * 180) / Math.PI + 90
    if (rotation < 0) rotation += 360

    if (rotation >= 85 && rotation <= 95) {
      rotation = 90
    } else if (rotation >= 175 && rotation <= 185) {
      rotation = 180
    } else if (rotation >= 265 && rotation <= 275) {
      rotation = 270
    } else if (rotation >= 355 || rotation <= 5) {
      rotation = 0
    }

    return map.actions.updateMapObjects({
      floorId: map.floorId,
      updatedObjects: [{ ...bi, map: { ...bi.map, rotation: Math.round(rotation) } }],
    })
  }
}

export default MapObjectDrag
